import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {AbstractControl, FormControl, FormControlName} from '@angular/forms';
import {I18NextPipe} from 'angular-i18next';
import {fromEvent, Subject, Subscription} from 'rxjs';
import {iterable} from 'app/core/helpers/object';
import {CustomFormComponent} from 'app/shared/classes/custom-form-component';
import {takeUntil} from 'rxjs/operators';
import {FormService} from 'app/core/services/form.service';

interface ErrorMessageConfig {
    string: string;
    prepareArgs?(errorResult: any): {[key: string]: any};
}

/* istanbul ignore next */
const ERROR_MESSAGES = new Map(
    iterable<any, ErrorMessageConfig>({
        // ValidityState errors
        valueMissing: {
            string: 'validationMessages.valueMissing',
        },
        badInput: {
            string: 'validationMessages.badInput',
        },
        rangeUnderflow: {
            string: 'validationMessages.rangeUnderflow',
            prepareArgs: result => ({min: result.min}),
        },
        rangeOverflow: {
            string: 'validationMessages.rangeOverflow',
            prepareArgs: result => ({max: result.max}),
        },
        stepMismatch: {
            string: 'validationMessages.stepMismatch',
            prepareArgs: result => ({step: result.step}),
        },
        patternMismatch: {
            string: 'validationMessages.patternMismatch',
        },
        typeMismatch: {
            string: 'validationMessages.typeMismatch',
            prepareArgs: result => ({type: result.type}),
        },
        tooLong: {
            string: 'validationMessages.tooLong',
            prepareArgs: result => ({maxLength: result.maxLength}),
        },
        tooShort: {
            string: 'validationMessages.tooShort',
            prepareArgs: result => ({minLength: result.minLength}),
        },

        // Angular errors
        required: {
            string: 'validationMessages.valueMissing',
        },
        min: {
            string: 'validationMessages.rangeUnderflow',
            prepareArgs: result => ({min: result.min}),
        },
        max: {
            string: 'validationMessages.rangeOverflow',
            prepareArgs: result => ({max: result.max}),
        },
        pattern: {
            string: 'validationMessages.patternMismatch',
        },
        maxlength: {
            string: 'validationMessages.tooLong',
            prepareArgs: result => ({maxLength: result.requiredLength}),
        },
        minlength: {
            string: 'validationMessages.tooShort',
            prepareArgs: result => ({minLength: result.requiredLength}),
        },
    })
);

const EQUIVALENTS = [
    new Set(['required', 'valueMissing']),
    new Set(['pattern', 'patternMismatch']),
    new Set(['maxlength', 'tooLong']),
    new Set(['minlength', 'tooShort']),
    new Set(['max', 'rangeOverflow']),
    new Set(['min', 'rangeUnderflow']),
];

// hack to set input nativeElement to FormControl
const originFormControlNameNgOnChanges = FormControlName.prototype.ngOnChanges;

FormControlName.prototype.ngOnChanges = function () {
    const result = originFormControlNameNgOnChanges.apply(this, arguments);

    this.control.nativeElement = this.valueAccessor._elementRef?.nativeElement || (this.valueAccessor as CustomFormComponent).getInput();
    return result;
};

@Component({
    selector: 'input-errors',
    templateUrl: './input-errors.component.html',
    styleUrls: ['./input-errors.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class InputErrorsComponent implements OnChanges, OnDestroy, OnInit {
    @Input() control: FormControl;
    @Input() immediate: boolean;
    @Input() clearServerErrorsOnBlur: boolean;

    errors: Array<{name: string; message: string}>;
    private changeSubscription = Subscription.EMPTY;
    private originalMarkAsTouched: (opts?: {onlySelf?: boolean}) => void;
    private destroy$ = new Subject();

    trackByFn = (index, item: {name: string; message: string}) => item.message;

    constructor(
        private i18next: I18NextPipe,
        private cd: ChangeDetectorRef,
        private formService: FormService
    ) {}

    ngOnInit() {
        if (!this.clearServerErrorsOnBlur) {
            return;
        }

        this.cleanServerErrorOnInputBlur();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.control.previousValue) {
            this.cleanUp(changes.control.previousValue);
        }
        if (changes.control.currentValue) {
            this.originalMarkAsTouched = this.control.markAsTouched;
            this.control.markAsTouched = (...args) => {
                this.control.setValue(this.control.value, {
                    emitEvent: true,
                });
                if (typeof this.originalMarkAsTouched === 'function') {
                    this.originalMarkAsTouched.apply(this.control, args);
                    this.control.markAsTouched = this.originalMarkAsTouched;
                }
                this.originalMarkAsTouched = null;
            };
            this.changeSubscription = this.control.valueChanges.subscribe(() => {
                this.parseErrors();
            });
            if (this.immediate) {
                this.parseErrors();
            }
        }
    }

    cleanServerErrorOnInputBlur(): void {
        const inputElement = (this.control as FormControl & {nativeElement: any}).nativeElement;

        if (!inputElement) {
            return;
        }

        fromEvent(inputElement, 'blur')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const errorWithoutServer = {...this.control.errors};
                delete errorWithoutServer.serverError;
                this.control.setErrors(Object.keys(errorWithoutServer).length ? errorWithoutServer : null);
                this.formService.clearControlErrors(this.control);
                this.parseErrors();
            });
    }

    cleanUp(control: AbstractControl) {
        if (this.originalMarkAsTouched && control) {
            control.markAsTouched = this.originalMarkAsTouched;
        }
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
    }

    ngOnDestroy() {
        this.cleanUp(this.control);
        this.destroy$.next();
    }

    parseErrors() {
        if (this.control.errors) {
            let parsed = [];
            this.errors = Object.entries(this.control.errors).reduce((errors, [name, errorResult]) => {
                if (!this.isEquivalentUsed(name, parsed)) {
                    let errorConfig = ERROR_MESSAGES.get(name);
                    errors.push({
                        name,
                        message: errorConfig
                            ? this.i18next.transform(
                                  errorConfig.string,
                                  errorConfig.prepareArgs ? errorConfig.prepareArgs(errorResult) : errorResult
                              )
                            : errorResult,
                    });
                    parsed.push(name);
                }
                return errors;
            }, []);
        } else {
            this.errors = [];
        }
        this.cd.markForCheck();
    }

    private isEquivalentUsed(name: string, alreadyUsed: Array<string>): boolean {
        let equivalents = EQUIVALENTS.find(set => set.has(name));
        if (equivalents) {
            return alreadyUsed.some(usedName => equivalents.has(usedName));
        } else {
            return alreadyUsed.includes(name);
        }
    }
}
